import React from 'react';
import { observer } from 'mobx-react-lite';
import { Grid, InputAdornment, MenuItem, TextField, Typography } from '@material-ui/core';
import { IOccupation, ProofOfOccupationType } from 'realhaus-sdk';
import { ProofOfOccupationSelectForm } from './documentUploadDialog';
import { ConfirmDialog } from '../uiComponents/confirmationDialog';
import { DatePicker } from '../datePicker/datepicker';
import { DocumentViewerField } from '../document/documentViewField';

interface TenantEmploymentProps {
    employment: IOccupation;
    updateEmployment: (prop: keyof IOccupation) => (value: unknown) => void;
    isEdit?: boolean;
}

export const TenantEmployment: React.FC<TenantEmploymentProps> = observer(
    ({ employment, updateEmployment, isEdit = false }) => {
        const [selectedProofOfOccupation, setSelectedProofOfOccupation] = React.useState<{
            type: ProofOfOccupationType;
            file: File;
        }>();

        const [selectedStartDate, setSelectedStartDate] = React.useState<Date>(
            employment.startDate ? new Date(employment.startDate) : new Date(),
        );

        const handleStartDateChange = (date: Date | null) => {
            setSelectedStartDate(date ?? new Date(Date.now()));
            updateEmployment('startDate')(date?.getTime() ?? 0);
        };

        const updateProp =
            (prop: keyof IOccupation) =>
            (
                ev:
                    | React.ChangeEvent<{ value: unknown }>
                    | { type: ProofOfOccupationType; file: File },
            ) => {
                let value: unknown;
                if ('target' in ev && ev.target) {
                    value = prop === 'income' ? Number(ev.target.value) : ev.target.value;
                } else {
                    value = ev;
                }
                updateEmployment(prop)(value);
            };

        return (
            <>
                <Grid container spacing={2} alignItems='center' justifyContent='center'>
                    <Grid item xs={12}>
                        <Typography variant='subtitle1' gutterBottom>
                            Start Date
                        </Typography>
                        <DatePicker
                            id='start-date-picker'
                            selectedDate={selectedStartDate}
                            onChange={handleStartDateChange}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant='subtitle1' gutterBottom>
                            Employment Status
                        </Typography>
                        <TextField
                            select
                            margin='normal'
                            id='status'
                            value={employment?.status ?? ''}
                            onChange={updateProp('status')}
                            fullWidth
                        >
                            <MenuItem value='employed'>Employed</MenuItem>
                            <MenuItem value='self-employed'>Self-Employed</MenuItem>
                            <MenuItem value='retired'>Retired</MenuItem>
                            <MenuItem value='unemployed'>Unemployed</MenuItem>
                            <MenuItem value='student'>Student</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant='subtitle1' gutterBottom>
                            Employer
                        </Typography>
                        <TextField
                            id='employer'
                            placeholder='Employer'
                            fullWidth
                            margin='dense'
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={employment?.employer ?? ''}
                            onChange={updateProp('employer')}
                            required
                            disabled={isEdit}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant='subtitle1' gutterBottom>
                            Manager
                        </Typography>
                        <TextField
                            id='manager'
                            placeholder='Supervisor/Manager Name'
                            fullWidth
                            margin='dense'
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={employment?.manager ?? ''}
                            onChange={updateProp('manager')}
                            required
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant='subtitle1' gutterBottom>
                            Type of Business
                        </Typography>
                        <TextField
                            id='businessType'
                            placeholder='Type of Business'
                            fullWidth
                            margin='dense'
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={employment?.businessType ?? ''}
                            onChange={updateProp('businessType')}
                            required
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant='subtitle1' gutterBottom>
                            Job Title
                        </Typography>
                        <TextField
                            id='title'
                            placeholder='Job Title'
                            fullWidth
                            margin='dense'
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={employment?.title ?? ''}
                            onChange={updateProp('title')}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant='subtitle1' gutterBottom>
                            Employment Income
                        </Typography>
                        <Grid container spacing={2} alignItems='center' justifyContent='center'>
                            <Grid item xs={6}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Income
                                </Typography>
                                <TextField
                                    id='income'
                                    placeholder='e.g. 1400'
                                    fullWidth
                                    margin='dense'
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>$</InputAdornment>
                                        ),
                                    }}
                                    value={employment?.income ?? ''}
                                    onChange={updateProp('income')}
                                    type='number'
                                    required
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Income Type
                                </Typography>
                                <TextField
                                    select
                                    margin='normal'
                                    id='incomeType'
                                    value={employment?.incomeType ?? ''}
                                    onChange={updateProp('incomeType')}
                                    fullWidth
                                >
                                    <MenuItem value='weekly'>Weekly</MenuItem>
                                    <MenuItem value='bi-weekly'>Bi Weekly</MenuItem>
                                    <MenuItem value='monthly'>Monthly</MenuItem>
                                    <MenuItem value='yearly'>Yearly</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant='subtitle1' gutterBottom>
                            Proof Of Employment
                        </Typography>

                        <ProofOfOccupationSelectForm
                            onDocumentSelected={(file, type) => {
                                setSelectedProofOfOccupation({
                                    type,
                                    file,
                                });

                                if (!employment.proofOfOccupation?.fileUrl) {
                                    updateProp('proofOfOccupation')({ type, file } as any);
                                    setSelectedProofOfOccupation(undefined);
                                }
                            }}
                        />

                        <ConfirmDialog
                            title='Confirm Replace Document'
                            content={
                                <Typography>
                                    Are you sure you want to replace the existing proof of
                                    occupation document? This action cannot be undone.
                                </Typography>
                            }
                            openDialog={
                                !!selectedProofOfOccupation &&
                                !!employment.proofOfOccupation?.fileUrl
                            }
                            closeDialog={(confirm: boolean) => () => {
                                if (confirm && !!selectedProofOfOccupation) {
                                    updateProp('proofOfOccupation')({
                                        type: selectedProofOfOccupation.type,
                                        file: selectedProofOfOccupation.file,
                                    });
                                }

                                setSelectedProofOfOccupation(undefined);
                            }}
                        />

                        {!!employment.proofOfOccupation?.fileUrl && (
                            <DocumentViewerField
                                title={employment.proofOfOccupation?.type || ''}
                                url={employment.proofOfOccupation?.fileUrl || ''}
                            />
                        )}
                    </Grid>
                </Grid>
            </>
        );
    },
);
